import * as React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications, type NotificationPreference } from '@discovr/api';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Checkbox } from '../ui/checkbox';
import { Button } from '../ui/button';
import { IconTooltipButton, SimpleTooltip } from '../ui/tooltip';
import { LoadingSpinner } from './LoadingSpinner';

// Notifications the backend produces + enforces preferences for. Only the
// Shopify events also send email and gate it on the same preference; the rest
// are in-app only. (The two transactional emails — campaign.created /
// creators.imported — are always-sent and intentionally not listed here.)
const EVENTS: { key: string; label: string; desc: string; brandOnly?: boolean; adminOnly?: boolean; teamOnly?: boolean; email?: boolean }[] = [
  { key: 'creator_approved', label: 'Creator decisions', desc: 'When a creator is approved or rejected.' },
  { key: 'script_uploaded', label: 'Script uploads', desc: 'When a script is uploaded for review.' },
  { key: 'script_approved', label: 'Script approvals', desc: 'When a script is approved.' },
  {
    key: 'script_reopened',
    label: 'Script reopened for review',
    desc: 'When an approved script is sent back for review.',
    brandOnly: true,
  },
  { key: 'content_uploaded', label: 'Content uploads', desc: 'When content is uploaded for review.' },
  { key: 'content_approved', label: 'Content approvals', desc: 'When content is approved.' },
  {
    key: 'content_reopened',
    label: 'Content reopened for review',
    desc: 'When approved content is sent back for review.',
    brandOnly: true,
  },
  {
    key: 'creator_moved_back',
    label: 'Creator moved back',
    desc: 'When Discovr moves a creator back to Brand approved or Pending.',
    brandOnly: true,
  },
  {
    key: 'shopify_store_access_lost',
    label: 'Shopify store disconnected',
    desc: 'When your Shopify store loses its connection to Discovr.',
    brandOnly: true,
    email: true,
  },
  {
    key: 'shopify_first_sale',
    label: 'First sale on a campaign',
    desc: "When a campaign's first paid order comes in.",
    brandOnly: true,
    email: true,
  },
  {
    key: 'shopify_usage_cap_reached',
    label: 'Discount code usage limit reached',
    desc: 'When a discount code hits its usage limit in Shopify.',
    brandOnly: true,
    email: true,
  },
  {
    key: 'shopify_codes_changed',
    label: 'Discount codes changed in Shopify',
    desc: 'When discount codes are renamed or removed directly in Shopify.',
    brandOnly: true,
    email: true,
  },
  {
    key: 'brand_join_requested',
    label: 'Join requests',
    desc: 'When someone asks to join your brand workspace.',
    brandOnly: true,
    teamOnly: true,
  },
  {
    key: 'admin_join_requested',
    label: 'Team join requests',
    desc: 'When someone asks to join the Discovr admin team.',
    adminOnly: true,
    teamOnly: true,
  },
  {
    key: 'campaign_access_changed',
    label: 'Campaign access changed',
    desc: 'When your access to a campaign is granted, changed or removed.',
  },
  { key: 'content_live', label: 'Content live', desc: 'When content goes live.' },
  { key: 'chat_reply', label: 'Chat messages', desc: 'New messages in campaign threads.' },
];

/**
 * NotificationPreferenceCenter — per-user notification toggles. Missing
 * rows default to on; toggling upserts via the preferences API. Shared by both
 * the brand and admin Settings pages.
 */
export function NotificationPreferenceCenter({
  lane = 'admin',
  canManageTeam = false,
}: {
  lane?: 'brand' | 'admin';
  /** Join-request notices only reach people who can approve them. */
  canManageTeam?: boolean;
}) {
  const queryClient = useQueryClient();

  const prefsQuery = useQuery({
    queryKey: ['notification-preferences'],
    queryFn: () => notifications.getPreferences(),
  });

  const update = useMutation({
    mutationFn: (p: NotificationPreference) => notifications.updatePreference(p),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notification-preferences'] }),
  });

  const stored = new Map<string, NotificationPreference>(
    (prefsQuery.data ?? []).map((p) => [p.event_type, p]),
  );
  const inApp = (key: string) => stored.get(key)?.in_app ?? true;
  const emailOn = (key: string) => stored.get(key)?.email ?? true;
  const loadFailed = prefsQuery.isError && !prefsQuery.data;
  const save = (key: string, patch: Partial<Pick<NotificationPreference, 'in_app' | 'email'>>) => {
    if (!prefsQuery.data || update.isPending) return;
    update.mutate({ event_type: key, in_app: inApp(key), email: emailOn(key), ...patch });
  };
  const events = EVENTS.filter(
    (e) =>
      (!e.brandOnly || lane === 'brand') &&
      (!e.adminOnly || lane === 'admin') &&
      (!e.teamOnly || canManageTeam),
  );

  return (
    <Card className="rounded-xl border border-border p-5">
      <CardHeader className="px-0 pt-0 pb-2">
        <CardTitle className="text-heading-h6">Notifications</CardTitle>
      </CardHeader>
      <CardContent className="px-0">
        {prefsQuery.isLoading ? (
          <LoadingSpinner size={24} className="text-muted-foreground" />
        ) : loadFailed ? (
          <div role="alert" className="flex flex-col items-start gap-3">
            <p className="text-body-sm-regular text-foreground-destructive-default">
              Couldn't load your notification settings. Please try again.
            </p>
            <Button variant="outline" size="sm" disabled={prefsQuery.isFetching} onClick={() => void prefsQuery.refetch()}>
              Retry
            </Button>
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {events.map((e) => (
              <li key={e.key} className="flex items-center justify-between gap-[18px] py-[15px] first:pt-0 last:pb-0">
                <div className="min-w-0">
                  <p className="text-body-sm-medium text-foreground">{e.label}</p>
                  <p className="text-caption-regular text-muted-foreground">{e.desc}</p>
                </div>
                <div className="flex shrink-0 items-center gap-6">
                  <label className="flex items-center gap-2 text-body-sm-regular text-foreground">
                    <Checkbox
                      checked={inApp(e.key)}
                      aria-disabled={update.isPending || undefined}
                      className={update.isPending ? 'opacity-50' : undefined}
                      aria-label={`In-app for ${e.label}`}
                      onCheckedChange={(v) => save(e.key, { in_app: v === true })}
                    />
                    In-app
                  </label>
                  {e.email ? (
                    <label className="flex items-center gap-2 text-body-sm-regular text-foreground">
                      <Checkbox
                        checked={emailOn(e.key)}
                        aria-disabled={update.isPending || undefined}
                        className={update.isPending ? 'opacity-50' : undefined}
                        aria-label={`Email for ${e.label}`}
                        onCheckedChange={(v) => save(e.key, { email: v === true })}
                      />
                      Email
                    </label>
                  ) : (
                    <span className="flex items-center gap-2 text-body-sm-regular text-muted-foreground">
                      <Checkbox checked={false} disabled aria-label={`Email for ${e.label}`} />
                      <SimpleTooltip content="Email not available for this notification">
                        <IconTooltipButton label={`Email not available for ${e.label}`} className="rounded-sm">
                          Email
                        </IconTooltipButton>
                      </SimpleTooltip>
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
        {update.isError && (
          <p role="alert" className="mt-4 text-body-sm-regular text-foreground-destructive-default">Couldn't save that change. Please try again.</p>
        )}
      </CardContent>
    </Card>
  );
}
